import { Component, computed, effect, inject, input, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { AppleMdmService } from '../../../../shared/services/apple-mdm-service';
import { AppleDevice } from '../../../../viewmodels/apple/apple-device.model';
import { CommandProgress } from '../../../../viewmodels/apple/signalr/command-context.model';

import { merge, Observable, Subject, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { PaneComponent } from '@rocksoft/cdk';
import { ToastService } from '@rocksoft/shared/toast';
import { OSSupportedPipe } from '../../../../pipes/os-supported.pipe';
import { AppleCommandProgressService } from '../../../../shared/services/apple-command-progress.service';
import { CommandSpinnerComponent } from '../../../../utilities/command-spinner/command-spinner.component';

@Component({
  selector: 'ml-mdm-apple-device-lock',
  templateUrl: './apple-device-lock.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [PaneComponent, CommandSpinnerComponent, ReactiveFormsModule, OSSupportedPipe],
})
export class AppleDeviceLockComponent implements OnDestroy {
  readonly apple = input.required<AppleDevice>();

  private readonly fb = inject(UntypedFormBuilder);
  private readonly mdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly toastService = inject(ToastService);

  public deviceLockForm: UntypedFormGroup;

  private readonly _udidSignal = computed(() => this.apple().udid);
  private _commandProgress: Subject<CommandProgress> = new Subject<CommandProgress>();
  readonly commandProgress$: Observable<CommandProgress> = toObservable(this._udidSignal).pipe(
    switchMap((_udid) =>
      merge(this._commandProgress, this.commandProgressService.commandsOfRequestType(_udid, 'DeviceLock'))
    )
  );

  success = false;

  private readonly subscriptions: Subscription[] = [];

  private osSupportedPipe = new OSSupportedPipe();

  constructor() {
    this.createForm();

    effect(() => {
      const apple = this.apple();
      if (!apple) return;

      this.onReset();

      if (this.osSupportedPipe.transform(apple, { macOS: '>=10.8' })) {
        this.deviceLockPin?.setValidators([Validators.required, Validators.minLength(6)]);
      } else {
        this.deviceLockPin?.setValidators(null);
      }
      this.deviceLockPin?.updateValueAndValidity();
    });
  }

  private createForm() {
    this.deviceLockForm = this.fb.group({
      deviceLockPin: [''],
      deviceLockMessage: [''],
      deviceLockPhoneNumber: [''],
    });
  }

  get deviceLockPin(): AbstractControl | null {
    return this.deviceLockForm.get('deviceLockPin');
  }

  get deviceLockMessage(): AbstractControl | null {
    return this.deviceLockForm.get('deviceLockMessage');
  }

  get deviceLockPhoneNumber(): AbstractControl | null {
    return this.deviceLockForm.get('deviceLockPhoneNumber');
  }

  onSubmit() {
    if (this.deviceLockForm.status !== 'VALID') {
      return;
    }

    this.deviceLockForm.disable();
    this.success = false;

    const command: CommandProgress = {
      udid: this.apple().udid,
      commandUUID: '',
      status: 'Sending',
      requestType: 'DeviceLock',
    };
    this._commandProgress.next(command);

    this.subscriptions.push(
      this.mdmService
        .lockDevice(
          this.apple().udid,
          this.deviceLockPin?.value,
          this.deviceLockMessage?.value,
          this.deviceLockPhoneNumber?.value
        )
        .subscribe({
          next: () => {
            this.success = true;
            this.deviceLockForm.enable();
            this.deviceLockForm.markAsPristine();
          },
          error: () => {
            this.deviceLockForm.enable();
            this.deviceLockForm.markAsPristine();
          },
        })
    );
  }

  private onReset(): void {
    this.deviceLockForm.reset({
      deviceLockPin: '',
      deviceLockMessage: '',
      deviceLockPhoneNumber: '',
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
